import LiveKitVideoRoom from './LiveKitVideoRoom';

import TelemedicineContext from '../../../context/telemedicine';
import useFinishCall from '../../../hooks/useFinishCall';
import { takePreparedRoom } from '../../../utils/livekit-room';
import { createRoomOptions } from '../../../utils/room-options';
import { discardPreviewTracks } from '../../WaitingRoom/preview-tracks';

import { showErrorToast } from '@amigoapp/doca-react';
import { RoomContext, usePersistentUserChoices } from '@livekit/components-react';
import { DisconnectReason, Room, RoomEvent } from 'livekit-client';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

const LiveKitVideoRoomContainer = ({ onConnectionLost }) => {
	const { telemedicine } = useContext(TelemedicineContext);
	const { userChoices } = usePersistentUserChoices();

	const [room] = useState(() => takePreparedRoom() || new Room(createRoomOptions({
		videoDeviceId: userChoices.videoDeviceId,
		audioDeviceId: userChoices.audioDeviceId
	})));

	const finishCall = useFinishCall();

	// Only the room being deleted means the doctor ended the call - any other drop keeps the
	// patient in it, on the reconnect screen. The patient has no hang-up button, so a disconnect
	// this client started only comes from the page unloading (F5, closing the tab) - nothing to
	// show for that.
	const onDisconnected = useCallback(reason => {
		if (reason === DisconnectReason.CLIENT_INITIATED) {
			return;
		}

		if (reason === DisconnectReason.ROOM_DELETED) {
			finishCall();

			return;
		}

		onConnectionLost(reason);
	}, [finishCall, onConnectionLost]);

	const onDisconnectedRef = useRef(onDisconnected);

	onDisconnectedRef.current = onDisconnected;

	const { url, token } = telemedicine.livekit;

	useEffect(() => {
		let leaving = false;

		const handleDisconnected = reason => {
			if (!leaving) {
				onDisconnectedRef.current(reason);
			}
		};

		room.on(RoomEvent.Disconnected, handleDisconnected);

		room.connect(url, token).catch(() => {
			if (!leaving) {
				showErrorToast('Não foi possível entrar na chamada.');

				onDisconnectedRef.current(DisconnectReason.JOIN_FAILURE);
			}
		});

		return () => {
			leaving = true;

			room.off(RoomEvent.Disconnected, handleDisconnected);
			room.disconnect();

			discardPreviewTracks();
		};
	}, [room, url, token]);

	return (
		<div className="lk-room-container">
			<RoomContext.Provider value={room}>
				<LiveKitVideoRoom />
			</RoomContext.Provider>
		</div>
	);
};

export default LiveKitVideoRoomContainer;
